/**
 * Event Registration Popup Module
 * ================================
 * Intercepts Add to Cart for event products and shows a
 * two-step registration popup (participant count → names).
 *
 * Dependencies: None (vanilla JS, no jQuery required)
 * Compatibility: All modern browsers, iOS Safari 14+, Chrome 80+
 *
 * Usage: Loaded via snippets/event-registration-init.liquid
 *        which passes product data to EventRegistration.init()
 */

(function () {
  'use strict';

  /* -------------------------------------------------------
     Configuration
     ------------------------------------------------------- */
  const CONFIG = {
    MIN_PARTICIPANTS: 1,
    MAX_PARTICIPANTS: 20,
    MAX_NAME_LENGTH: 100,
    CART_URL: '/cart',
    CART_ADD_URL: '/cart/add.js',
    SELECTORS: {
      popup: '#EventRegistrationPopup',
      overlay: '[data-er-close]',
      closeBtn: '[data-er-close]',
      step: '[data-er-step]',
      // Step 1 - Company
      companyInput: '[data-er-company-input]',
      companyValidationMsg: '[data-er-company-validation-msg]',
      toStep2: '[data-er-to-step-2]',
      // Step 2 - Count
      countInput: '[data-er-count-input]',
      increment: '[data-er-increment]',
      decrement: '[data-er-decrement]',
      countDisplay: '[data-er-count-display]',
      unitPrice: '[data-er-unit-price]',
      totalPrice: '[data-er-total-price]',
      toStep3: '[data-er-to-step-3]',
      backTo1: '[data-er-back-to-1]',
      // Step 3 - Names
      namesContainer: '[data-er-names-container]',
      countDisplay2: '[data-er-count-display-2]',
      totalPrice2: '[data-er-total-price-2]',
      validationMsg: '[data-er-validation-msg]',
      submitBtn: '[data-er-submit]',
      backTo2: '[data-er-back-to-2]',
      // Step 4 - Success
      successMsg: '[data-er-success-msg]',
      goToCart: '[data-er-go-to-cart]',
      // Loading
      loading: '[data-er-loading]',
      productName: '[data-er-product-name]',
      productName2: '[data-er-product-name-2]',
    },
  };

  /* -------------------------------------------------------
     State
     ------------------------------------------------------- */
  let state = {
    isOpen: false,
    currentStep: 1,
    count: 1,
    maxParticipants: CONFIG.MAX_PARTICIPANTS,
    company: '',
    variantId: null,
    productTitle: '',
    price: 0, // in cents (Shopify money format)
    moneyFormat: '{{amount}} DKK', // default; overridden by init
    formElement: null,
  };

  /* -------------------------------------------------------
     DOM references (cached on first open)
     ------------------------------------------------------- */
  let dom = {};

  function cacheDom() {
    const popup = document.querySelector(CONFIG.SELECTORS.popup);
    if (!popup) return false;

    dom.popup = popup;
    dom.overlay = popup.querySelector(CONFIG.SELECTORS.overlay);
    dom.closeBtns = popup.querySelectorAll(CONFIG.SELECTORS.closeBtn);
    dom.steps = popup.querySelectorAll(CONFIG.SELECTORS.step);
    // Step 1 - Company
    dom.companyInput = popup.querySelector(CONFIG.SELECTORS.companyInput);
    dom.companyValidationMsg = popup.querySelector(CONFIG.SELECTORS.companyValidationMsg);
    dom.toStep2 = popup.querySelector(CONFIG.SELECTORS.toStep2);
    // Step 2 - Count
    dom.countInput = popup.querySelector(CONFIG.SELECTORS.countInput);
    dom.increment = popup.querySelector(CONFIG.SELECTORS.increment);
    dom.decrement = popup.querySelector(CONFIG.SELECTORS.decrement);
    dom.countDisplays = popup.querySelectorAll(CONFIG.SELECTORS.countDisplay);
    dom.unitPrice = popup.querySelector(CONFIG.SELECTORS.unitPrice);
    dom.totalPrice = popup.querySelector(CONFIG.SELECTORS.totalPrice);
    dom.toStep3 = popup.querySelector(CONFIG.SELECTORS.toStep3);
    dom.backTo1 = popup.querySelector(CONFIG.SELECTORS.backTo1);
    // Step 3 - Names
    dom.namesContainer = popup.querySelector(CONFIG.SELECTORS.namesContainer);
    dom.countDisplay2 = popup.querySelector(CONFIG.SELECTORS.countDisplay2);
    dom.totalPrice2 = popup.querySelector(CONFIG.SELECTORS.totalPrice2);
    dom.validationMsg = popup.querySelector(CONFIG.SELECTORS.validationMsg);
    dom.submitBtn = popup.querySelector(CONFIG.SELECTORS.submitBtn);
    dom.backTo2 = popup.querySelector(CONFIG.SELECTORS.backTo2);
    // Step 4 - Success
    dom.successMsg = popup.querySelector(CONFIG.SELECTORS.successMsg);
    dom.goToCart = popup.querySelector(CONFIG.SELECTORS.goToCart);
    // Other
    dom.loading = popup.querySelector(CONFIG.SELECTORS.loading);
    dom.productName = popup.querySelector(CONFIG.SELECTORS.productName);
    dom.productName2 = popup.querySelector(CONFIG.SELECTORS.productName2);

    return true;
  }

  /* -------------------------------------------------------
     Money formatting
     ------------------------------------------------------- */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // Handle common Shopify money formats
    let formatted = state.moneyFormat
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString())
      .replace('{{amount}}', amount);
    return formatted;
  }

  /* -------------------------------------------------------
     Step management
     ------------------------------------------------------- */
  function goToStep(step) {
    state.currentStep = step;
    dom.steps.forEach((el) => {
      const stepNum = parseInt(el.getAttribute('data-er-step'), 10);
      el.classList.toggle('er-popup__step--active', stepNum === step);
    });

    // Focus management for accessibility
    if (step === 3) {
      const firstInput = dom.namesContainer.querySelector('input');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 100);
      }
    }
  }

  /* -------------------------------------------------------
     Update count & price displays
     ------------------------------------------------------- */
  function updateDisplays() {
    const count = state.count;
    const unitFormatted = formatMoney(state.price);
    const totalFormatted = formatMoney(state.price * count);

    dom.countInput.value = count;
    dom.countDisplays.forEach((el) => (el.textContent = count));
    dom.unitPrice.innerHTML = unitFormatted;
    dom.totalPrice.innerHTML = totalFormatted;

    if (dom.countDisplay2) dom.countDisplay2.textContent = count;
    if (dom.totalPrice2) dom.totalPrice2.innerHTML = totalFormatted;
  }

  /* -------------------------------------------------------
     Build name input fields
     ------------------------------------------------------- */
  function buildNameFields() {
    dom.namesContainer.innerHTML = '';
    for (let i = 1; i <= state.count; i++) {
      const field = document.createElement('div');
      field.className = 'er-popup__name-field';
      field.innerHTML = `
        <span class="er-popup__name-index">${i}</span>
        <input
          type="text"
          class="er-popup__name-input"
          placeholder="Deltager ${i} navn"
          data-er-name-input="${i}"
          maxlength="${CONFIG.MAX_NAME_LENGTH}"
          autocomplete="off"
          inputmode="text"
          required
        >
      `;
      dom.namesContainer.appendChild(field);
    }
  }

  /* -------------------------------------------------------
     Validation
     ------------------------------------------------------- */
  function validateNames() {
    const inputs = dom.namesContainer.querySelectorAll('[data-er-name-input]');
    let allValid = true;
    let firstEmpty = null;

    inputs.forEach((input) => {
      const value = input.value.trim();
      if (!value) {
        input.classList.add('er-popup__name-input--error');
        allValid = false;
        if (!firstEmpty) firstEmpty = input;
      } else {
        input.classList.remove('er-popup__name-input--error');
      }
    });

    if (!allValid) {
      dom.validationMsg.textContent = 'Indtast venligst et navn for hver deltager.';
      if (firstEmpty) firstEmpty.focus();
    } else {
      dom.validationMsg.textContent = '';
    }

    return allValid;
  }

  function getParticipantNames() {
    const inputs = dom.namesContainer.querySelectorAll('[data-er-name-input]');
    return Array.from(inputs).map((input) => input.value.trim());
  }

  /* -------------------------------------------------------
     Cart API: Add to cart with line item properties
     ------------------------------------------------------- */
  async function addToCart(variantId, quantity, names) {
    // Build properties object
    const properties = {
      _participants: JSON.stringify(names), // hidden, machine-readable
      Firma: state.company,
      Deltagere: String(quantity),
    };

    // Add individual participant names (human-readable)
    names.forEach((name, i) => {
      properties[`Deltager ${i + 1}`] = name;
    });

    const payload = {
      items: [
        {
          id: variantId,
          quantity: quantity,
          properties: properties,
        },
      ],
    };

    const response = await fetch(CONFIG.CART_ADD_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Failed to add to cart');
    }

    return response.json();
  }

  /* -------------------------------------------------------
     Update cart count in theme header
     ------------------------------------------------------- */
  function updateCartCount() {
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        // Impulse theme uses various selectors for cart count
        const countEls = document.querySelectorAll(
          '.cart-link__count, .site-header__cart-count, [data-cart-count], .cart-count-bubble span'
        );
        countEls.forEach((el) => {
          el.textContent = cart.item_count;
        });

        // Dispatch a custom event that Impulse may listen to
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { cart },
            bubbles: true,
          })
        );

        // Also try Impulse's built-in cart refresh if available
        if (window.theme && typeof window.theme.cart === 'object' && typeof window.theme.cart.getCart === 'function') {
          window.theme.cart.getCart();
        }
      })
      .catch(() => {});
  }

  /* -------------------------------------------------------
     Show / Hide loading
     ------------------------------------------------------- */
  function showLoading() {
    dom.loading.setAttribute('aria-hidden', 'false');
  }

  function hideLoading() {
    dom.loading.setAttribute('aria-hidden', 'true');
  }

  /* -------------------------------------------------------
     Open / Close popup
     ------------------------------------------------------- */
  function open(productData) {
    if (!dom.popup && !cacheDom()) return;

    state.variantId = productData.variantId;
    state.productTitle = productData.title;
    state.price = productData.price;
    state.moneyFormat = productData.moneyFormat || state.moneyFormat;
    state.count = 1;
    state.maxParticipants = productData.maxParticipants || CONFIG.MAX_PARTICIPANTS;
    state.formElement = productData.formElement || null;

    // Update counter input max attribute
    dom.countInput.setAttribute('max', state.maxParticipants);

    // Reset UI
    dom.productName.textContent = productData.title;
    if (dom.productName2) dom.productName2.textContent = productData.title;
    dom.companyInput.value = '';
    dom.companyValidationMsg.textContent = '';
    dom.validationMsg.textContent = '';
    hideLoading();
    updateDisplays();
    goToStep(1);

    // Show
    dom.popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    state.isOpen = true;

    // Trap focus
    setTimeout(() => {
      dom.companyInput.focus();
    }, 150);
  }

  function close() {
    if (!dom.popup) return;
    dom.popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    state.isOpen = false;

    // Return focus to triggering element
    if (state.formElement) {
      const btn = state.formElement.querySelector('[type="submit"], .add-to-cart, [data-er-add-to-cart], .product-form__cart-submit');
      if (btn) btn.focus();
    }
  }

  /* -------------------------------------------------------
     Event handlers
     ------------------------------------------------------- */
  function bindEvents() {
    if (!dom.popup) return;

    // Close buttons and overlay
    dom.closeBtns.forEach((btn) => {
      btn.addEventListener('click', close);
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.isOpen) {
        close();
      }
    });

    // Counter buttons
    dom.increment.addEventListener('click', () => {
      if (state.count < state.maxParticipants) {
        state.count++;
        updateDisplays();
      }
    });

    dom.decrement.addEventListener('click', () => {
      if (state.count > CONFIG.MIN_PARTICIPANTS) {
        state.count--;
        updateDisplays();
      }
    });

    // Step 1 → Step 2: Validate company, then go to count
    dom.toStep2.addEventListener('click', () => {
      const company = dom.companyInput.value.trim();
      if (!company) {
        dom.companyInput.classList.add('er-popup__name-input--error');
        dom.companyValidationMsg.textContent = 'Indtast venligst et firmanavn.';
        dom.companyInput.focus();
        return;
      }
      dom.companyInput.classList.remove('er-popup__name-input--error');
      dom.companyValidationMsg.textContent = '';
      state.company = company;
      updateDisplays();
      goToStep(2);
    });

    // Clear company validation on input
    dom.companyInput.addEventListener('input', () => {
      dom.companyInput.classList.remove('er-popup__name-input--error');
      if (dom.companyValidationMsg.textContent) {
        dom.companyValidationMsg.textContent = '';
      }
    });

    // Step 2 → Step 1: Back to company
    dom.backTo1.addEventListener('click', () => {
      goToStep(1);
    });

    // Step 2 → Step 3: Go to names
    dom.toStep3.addEventListener('click', () => {
      buildNameFields();
      updateDisplays();
      goToStep(3);
    });

    // Step 3 → Step 2: Back to count
    dom.backTo2.addEventListener('click', () => {
      goToStep(2);
    });

    // Submit (add to cart)
    dom.submitBtn.addEventListener('click', async () => {
      if (!validateNames()) return;

      const names = getParticipantNames();
      showLoading();
      dom.submitBtn.disabled = true;

      try {
        await addToCart(state.variantId, state.count, names);
        updateCartCount();

        // Show success
        dom.successMsg.textContent = `${state.count} deltager${state.count > 1 ? 'e' : ''} fra ${state.company} tilmeldt ${state.productTitle}.`;
        goToStep(4);
      } catch (err) {
        dom.validationMsg.textContent = err.message || 'Noget gik galt. Prøv venligst igen.';
        console.error('[EventRegistration]', err);
      } finally {
        hideLoading();
        dom.submitBtn.disabled = false;
      }
    });

    // Go to cart
    dom.goToCart.addEventListener('click', () => {
      window.location.href = CONFIG.CART_URL;
    });

    // Clear validation errors on input
    dom.namesContainer.addEventListener('input', (e) => {
      if (e.target.matches('[data-er-name-input]')) {
        e.target.classList.remove('er-popup__name-input--error');
        if (dom.validationMsg.textContent) {
          dom.validationMsg.textContent = '';
        }
      }
    });
  }

  /* -------------------------------------------------------
     Form interception
     -------------------------------------------------------
     Impulse theme handles Add to Cart via its own JS bound
     to [data-add-to-cart] elements. We must intercept BEFORE
     Impulse's handlers fire. Strategy:

     1. Use capture phase (fires before bubble-phase listeners)
     2. Use stopImmediatePropagation (prevents other listeners
        on the same element from firing)
     3. Intercept both click and submit events
     4. Mark the form so we can identify intercepted forms
     ------------------------------------------------------- */
  function interceptForm(form, productData) {
    // Mark the form so we know it's an event product form
    form.setAttribute('data-er-event-form', 'true');

    // Store product data on the form for retrieval
    form._erProductData = productData;

    // Get the submit button
    const submitBtn = form.querySelector(
      '[type="submit"], [data-add-to-cart], [data-er-add-to-cart], .add-to-cart, .product-form__cart-submit'
    );

    // Helper to open popup with current variant
    function openPopup(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Read the currently selected variant ID fresh from the form
      const variantInput = form.querySelector('[name="id"]');
      if (variantInput) {
        productData.variantId = parseInt(variantInput.value, 10);
      }

      open({
        ...productData,
        formElement: form,
      });

      return false;
    }

    // Intercept form submit — capture phase fires FIRST
    form.addEventListener('submit', openPopup, { capture: true });

    // Intercept button click — capture phase on the button itself
    if (submitBtn) {
      submitBtn.addEventListener('click', openPopup, { capture: true });
    }

    // Safety net: Impulse may also use a delegated listener on a parent.
    // Add a document-level capture listener that catches clicks on
    // [data-add-to-cart] inside our marked forms.
    if (!window._erDocListenerAdded) {
      window._erDocListenerAdded = true;
      document.addEventListener('click', function (e) {
        const addBtn = e.target.closest('[data-add-to-cart], [data-er-add-to-cart]');
        if (!addBtn) return;

        const parentForm = addBtn.closest('form[data-er-event-form]');
        if (!parentForm) return; // Not an event form, let it through

        const pData = parentForm._erProductData;
        if (!pData || !pData.isEvent) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const variantInput = parentForm.querySelector('[name="id"]');
        if (variantInput) {
          pData.variantId = parseInt(variantInput.value, 10);
        }

        open({
          ...pData,
          formElement: parentForm,
        });
      }, { capture: true });
    }

    console.log('[EventRegistration] Intercepting form for:', productData.title);
  }

  /* -------------------------------------------------------
     Public API
     ------------------------------------------------------- */
  window.EventRegistration = {
    /**
     * Initialize the popup for a specific product form.
     *
     * @param {Object} options
     * @param {HTMLFormElement} options.form - The product form element
     * @param {boolean} options.isEvent - Whether this product is an event
     * @param {number} options.variantId - Default variant ID
     * @param {string} options.title - Product title
     * @param {number} options.price - Price in cents (e.g., 10000 = 100.00 DKK)
     * @param {string} [options.moneyFormat] - Shopify money format string
     */
    init: function (options) {
      if (!options || !options.form) {
        console.warn('[EventRegistration] No form element provided.');
        return;
      }

      // Cache DOM on first init
      if (!dom.popup) {
        if (!cacheDom()) {
          console.warn('[EventRegistration] Popup markup not found. Is event-registration-popup.liquid included?');
          return;
        }
        bindEvents();
      }

      if (options.isEvent) {
        interceptForm(options.form, {
          isEvent: true,
          variantId: options.variantId,
          title: options.title,
          price: options.price,
          moneyFormat: options.moneyFormat,
          maxParticipants: options.maxParticipants || CONFIG.MAX_PARTICIPANTS,
        });
      }
    },

    // Expose for programmatic control
    open: open,
    close: close,
  };
})();
